import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collectionService, groupService } from "@/services";
import type { CollectionDemand, KametiGroup } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { canManageFinancials } from "@/config/roles";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { Modal } from "@/components/base/Modal";
import { Textarea } from "@/components/base/Textarea";
import { ErrorState } from "@/components/base/ErrorState";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";
import { Select, type SelectOption } from "@/components/base/Select";
import { FilterBar } from "@/components/feature/FilterBar";

export default function LateFeesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useAuth();
  const toast = useToast();
  const canManage = canManageFinancials(role);

  const groupIdParam = searchParams.get("groupId") || "";
  const [groups, setGroups] = useState<KametiGroup[]>([]);
  const [search, setSearch] = useState("");
  const [demands, setDemands] = useState<CollectionDemand[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CollectionDemand[]>([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyReason, setApplyReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [waiveTarget, setWaiveTarget] = useState<CollectionDemand | null>(null);
  const [waiveReason, setWaiveReason] = useState("");

  useEffect(() => {
    groupService
      .listGroups()
      .then(setGroups)
      .catch(() => setGroups([]));
  }, []);

  const currentGroup = useMemo(
    () => groups.find((g) => g.id === groupIdParam),
    [groups, groupIdParam]
  );

  const groupOptions = useMemo<SelectOption[]>(() => {
    return [
      { value: "all", label: "All groups" },
      ...groups.map((g) => ({
        value: g.id,
        label: `${g.name} (${g.code})`,
      })),
    ];
  }, [groups]);

  const handleGroupChange = (val: string) => {
    const next = new URLSearchParams(searchParams);
    if (!val || val === "all") {
      next.delete("groupId");
    } else {
      next.set("groupId", val);
    }
    setSearchParams(next);
    setSelected([]);
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      setDemands(await collectionService.listOverdueDemands(groupIdParam || undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load overdue demands.");
      setDemands([]);
    }
  }, [groupIdParam]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredDemands = useMemo(() => {
    if (!demands) return null;
    if (!search.trim()) return demands;
    const q = search.toLowerCase();
    return demands.filter(
      (d) =>
        d.memberName.toLowerCase().includes(q) ||
        d.memberCode.toLowerCase().includes(q) ||
        d.groupName.toLowerCase().includes(q) ||
        d.groupCode.toLowerCase().includes(q) ||
        d.ticketNumber.includes(q)
    );
  }, [demands, search]);

  const applyable = useMemo(
    () => (filteredDemands ?? []).filter((d) => !d.hasLateFeeApplied && d.applicableLateFee > 0),
    [filteredDemands]
  );
  const selectedApplyable = useMemo(
    () => selected.filter((d) => !d.hasLateFeeApplied && d.applicableLateFee > 0),
    [selected]
  );
  const applyTotal = useMemo(() => selectedApplyable.reduce((s, d) => s + d.applicableLateFee, 0), [selectedApplyable]);

  const doApply = async () => {
    if (!applyReason.trim()) {
      toast.error("Reason required", "You must explain why these fees are being applied.");
      return;
    }
    setSubmitting(true);
    try {
      for (const d of selectedApplyable) {
        await collectionService.applyLateFee(d.id, d.applicableLateFee, applyReason, "Staff User", groupIdParam || undefined);
      }
      toast.success("Late fees applied", `${selectedApplyable.length} fee(s) applied (₹${applyTotal.toLocaleString("en-IN")}).`);
      setApplyOpen(false);
      setApplyReason("");
      setSelected([]);
      await load();
    } catch (err) {
      toast.error("Could not apply fees", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const doWaive = async () => {
    if (!waiveTarget) return;
    if (!waiveReason.trim()) {
      toast.error("Reason required", "You must explain why this fee is being waived.");
      return;
    }
    setSubmitting(true);
    try {
      await collectionService.waiveLateFee(waiveTarget.id, waiveReason, "Staff User", groupIdParam || undefined);
      toast.info("Late fee waived", `The fee on ${waiveTarget.memberName} was waived.`);
      setWaiveTarget(null);
      setWaiveReason("");
      await load();
    } catch (err) {
      toast.error("Could not waive fee", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const columns: DataTableColumn<CollectionDemand>[] = [
    {
      id: "member",
      header: "Member",
      accessor: (d) => (
        <div>
          <span className="font-medium text-foreground-900">{d.memberName}</span>
          <span className="tnum ml-2 text-xs text-foreground-400">{d.memberCode}</span>
        </div>
      ),
      sortValue: (d) => d.memberName,
      sortable: true,
    },
    {
      id: "group",
      header: "Group",
      accessor: (d) => (
        <div>
          <span className="text-sm">{d.groupName}</span>
          {d.cycle > 0 ? <span className="tnum ml-2 text-xs text-foreground-400">C{d.cycle}</span> : null}
        </div>
      ),
      sortValue: (d) => d.groupName,
      sortable: true,
    },
    {
      id: "due",
      header: "Due date",
      accessor: (d) => <span className="tnum">{formatDate(d.dueDate)}</span>,
      sortValue: (d) => d.dueDate,
      sortable: true,
    },
    {
      id: "days",
      header: "Days overdue",
      align: "right",
      accessor: (d) => <span className="tnum font-medium text-danger-600">{d.daysOverdue}</span>,
      sortValue: (d) => d.daysOverdue,
      sortable: true,
    },
    {
      id: "balance",
      header: "Balance",
      align: "right",
      accessor: (d) => <CurrencyDisplay amount={d.balance} hideSymbol />,
      sortValue: (d) => d.balance,
      sortable: true,
    },
    {
      id: "fee",
      header: "Applicable fee",
      align: "right",
      accessor: (d) => (
        <CurrencyDisplay amount={d.applicableLateFee} hideSymbol tone={d.applicableLateFee > 0 ? "danger" : "default"} />
      ),
      sortValue: (d) => d.applicableLateFee,
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      accessor: (d) =>
        d.hasLateFeeApplied ? (
          <Badge variant="paid" dot>Fee applied</Badge>
        ) : (
          <Badge variant="pending">No fee</Badge>
        ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      width: "96px",
      hideable: false,
      accessor: (d) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="outline"
            icon={<i className="ri-close-circle-line" />}
            disabled={!d.hasLateFeeApplied || !canManage}
            onClick={() => {
              setWaiveReason("");
              setWaiveTarget(d);
            }}
          >
            Waive
          </Button>
        </div>
      ),
    },
  ];

  const backUrl = groupIdParam ? `/collection/groups/${groupIdParam}` : "/collection";
  const backLabel = currentGroup ? `Back to ${currentGroup.name}` : "Back to collection";

  const breadcrumb = (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Collection" to="/collection" />
      {currentGroup ? (
        <BreadcrumbItem label={currentGroup.name} to={`/collection/groups/${currentGroup.id}`} />
      ) : null}
      <BreadcrumbItem label="Late fees" isLast />
    </>
  );

  return (
    <div>
      <PageHeader
        breadcrumb={breadcrumb}
        title="Late fees"
        subtitle="Demands past their due date plus the group's grace period and still unpaid"
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              icon={<i className="ri-arrow-left-line" />}
              onClick={() => navigate(backUrl)}
            >
              {backLabel}
            </Button>
            {!canManage ? (
              <span className="text-xs text-foreground-400">Read-only — reversal & waiver need Admin/Accountant</span>
            ) : null}
            <Button
              variant="primary"
              icon={<i className="ri-add-line" />}
              disabled={selectedApplyable.length === 0 || !canManage}
              onClick={() => setApplyOpen(true)}
            >
              Apply to {selectedApplyable.length > 0 ? `${selectedApplyable.length} selected` : "selected"}
            </Button>
          </>
        }
      />

      <div className="space-y-4">
        <FilterBar
          search={{
            value: search,
            onChange: setSearch,
            placeholder: "Search member, ticket or group…",
          }}
          filters={[
            {
              id: "group",
              control: (
                <Select
                  className="w-64"
                  value={groupIdParam || "all"}
                  onChange={handleGroupChange}
                  options={groupOptions}
                  searchable
                  placeholder="Filter by group…"
                  prefix={<i className="ri-team-line text-foreground-400" />}
                />
              ),
            },
          ]}
          activeCount={(groupIdParam ? 1 : 0) + (search ? 1 : 0)}
          onClear={() => {
            setSearch("");
            handleGroupChange("all");
          }}
        />

        {groupIdParam ? (
          <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-xs text-amber-900">
            <div className="flex items-center gap-2">
              <i className="ri-filter-3-line text-sm text-amber-600" />
              <span>
                Filtered by group: <span className="font-semibold">{currentGroup?.name ?? groupIdParam}</span>
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-amber-300 hover:bg-amber-100"
              onClick={() => handleGroupChange("all")}
            >
              Show all groups
            </Button>
          </div>
        ) : null}

        {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

        {!filteredDemands ? (
          <div className="h-64 animate-pulse rounded-lg bg-background-200" />
        ) : (
          <Card
            title={currentGroup ? `Overdue demands · ${currentGroup.name}` : "Overdue demands"}
            actions={
              <span className="text-xs text-foreground-400">
                {applyable.length} demand(s) eligible for a fee
              </span>
            }
          >
            <DataTable<CollectionDemand>
              columns={columns}
              rows={filteredDemands}
              rowKey={(d) => d.id}
              emptyMessage={currentGroup ? `No overdue demands in ${currentGroup.name}` : "No overdue demands"}
              emptyDescription={
                currentGroup
                  ? "All members of this group are either paid or within their grace period."
                  : "Everything is either paid or within its grace period. Nice and clear."
              }
              selectable
              onSelectionChange={(rows) => setSelected(rows)}
              showTotals
              totalLabel="Total"
              defaultPageSize={15}
            />
          </Card>
        )}
      </div>

      <Modal
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        title="Apply late fees"
        description={`${selectedApplyable.length} selected · total ₹${applyTotal.toLocaleString("en-IN")}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setApplyOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="danger" icon={<i className="ri-add-line" />} loading={submitting} onClick={doApply}>
              Apply fees
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-md bg-background-100/60 px-3 py-2.5 text-xs text-foreground-600">
            Each group's own policy is used — flat or percentage, and per-day where configured. Fees can be waived later
            for members with a genuine reason.
          </div>
          <Textarea
            label="Reason for applying fees"
            value={applyReason}
            onChange={(e) => setApplyReason(e.target.value)}
            placeholder="e.g. Arrears from cycle 8 across 6 members…"
            rows={4}
            maxLength={500}
          />
        </div>
      </Modal>

      <Modal
        open={Boolean(waiveTarget)}
        onClose={() => setWaiveTarget(null)}
        title="Waive this late fee?"
        description={
          waiveTarget
            ? `${waiveTarget.memberName} · ${waiveTarget.groupName}${waiveTarget.cycle > 0 ? ` C${waiveTarget.cycle}` : ""}`
            : ""
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setWaiveTarget(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" icon={<i className="ri-close-circle-line" />} loading={submitting} onClick={doWaive}>
              Waive fee
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-md bg-warning-50 px-3 py-2.5 text-xs text-warning-800">
            The waived fee is removed from the demand and the member's balance is reduced accordingly. The waiver is
            recorded for the audit trail.
          </div>
          <Textarea
            label="Waiver reason (required)"
            value={waiveReason}
            onChange={(e) => setWaiveReason(e.target.value)}
            placeholder="e.g. Member requested waiver after a medical emergency…"
            rows={4}
            maxLength={500}
          />
        </div>
      </Modal>
    </div>
  );
}